import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  CreateScheduleInput,
  ScreenSchedule,
  AllSchedulesFilters,
  AllSchedulesResult,
} from "../../schemas/scheduleSchema";

const GET_SCREEN_SCHEDULES = gql`
  query ScreenSchedules($screenId: ID!, $date: String) {
    screenSchedules(screenId: $screenId, date: $date) {
      scheduleDate
      screenID
      schedule {
        slots {
          startTime
          endTime
          playlist {
            playlistName
            _id
            id
            isSuperseded
            supersededWindows
            playlist {
              playlistName
              _id
              playOrder
              playlist {
                creativeName
                status
                customerName
                playOrder
                isPaused
              }
            }
          }
        }
      }
      clientPlaylist {
        id
        name
        type
        duration
        isActive
        playlist {
          creativeName
          customerName
          playOrder
          status
          isPaused
        }
      }
    }
  }
`;

const GET_ALL_SCHEDULES = gql`
  query AllSchedules(
    $page: Int
    $pageSize: Int
    $screenId: ID
    $playlistId: ID
    $status: ScheduleStatusFilter
  ) {
    allSchedules(
      page: $page
      pageSize: $pageSize
      screenId: $screenId
      playlistId: $playlistId
      status: $status
    ) {
      schedules {
        batchId
        playlistId
        playlistName
        startDate
        endDate
        startTime
        endTime
        daysOfWeek
        isOverride
        createdBy
        createdAt
        screens {
          id
          screenName
        }
        scheduleIds
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

export const CREATIVE_DOWNLOAD_STATUS_UPDATED = gql`
  subscription CreativeDownloadStatusUpdated($screenId: ID!) {
    creativeDownloadStatusUpdated(screenId: $screenId) {
      screenId
      updatedAt
      reason
    }
  }
`;

const DELETE_SCHEDULE = gql`
  mutation DeleteSchedule($id: ID!) {
    deleteSchedule(id: $id)
  }
`;

const DELETE_SCHEDULE_FOR_DATE = gql`
  mutation DeleteScheduleForDate($id: ID!, $date: String!) {
    deleteScheduleForDate(id: $id, date: $date)
  }
`;

const DELETE_SCHEDULE_BATCH = gql`
  mutation DeleteScheduleBatch($batchId: ID!) {
    deleteScheduleBatch(batchId: $batchId)
  }
`;

const DELETE_SCHEDULE_BATCH_FOR_SCREENS = gql`
  mutation DeleteScheduleBatchForScreens($batchId: ID!, $screenIds: [ID!]!) {
    deleteScheduleBatchForScreens(batchId: $batchId, screenIds: $screenIds)
  }
`;

const DELETE_ALL_SCHEDULES_FOR_SCREEN = gql`
  mutation DeleteAllSchedulesForScreen($screenId: ID!) {
    deleteAllSchedulesForScreen(screenId: $screenId)
  }
`;

const CREATE_SCHEDULE = gql`
  mutation CreateSchedule($input: CreateScheduleInput!) {
    createSchedule(input: $input)
  }
`;

export const screenPlaylistService = {
  remove: async (id: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_SCHEDULE,
      variables: { id },
    });
    return data.deleteSchedule;
  },

  removeForDate: async (id: string, date: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_SCHEDULE_FOR_DATE,
      variables: { id, date },
    });
    return data.deleteScheduleForDate;
  },

  removeBatch: async (batchId: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_SCHEDULE_BATCH,
      variables: { batchId },
    });
    return data.deleteScheduleBatch;
  },

  removeBatchForScreens: async (
    batchId: string,
    screenIds: string[],
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_SCHEDULE_BATCH_FOR_SCREENS,
      variables: { batchId, screenIds },
    });
    return data.deleteScheduleBatchForScreens;
  },

  removeAllForScreen: async (screenId: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_ALL_SCHEDULES_FOR_SCREEN,
      variables: { screenId },
    });
    return data.deleteAllSchedulesForScreen;
  },

  createSchedule: async (input: CreateScheduleInput): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_SCHEDULE,
      variables: { input },
    });
    return data.createSchedule;
  },

  getScreenSchedules: async (
    screenId: string,
    date?: string,
  ): Promise<ScreenSchedule[]> => {
    const { data } = await client.query({
      query: GET_SCREEN_SCHEDULES,
      variables: { screenId, date },
      fetchPolicy: "network-only",
    });
    return data.screenSchedules;
  },

  getAllSchedules: async (
    filters: AllSchedulesFilters = {},
  ): Promise<AllSchedulesResult> => {
    const { data } = await client.query({
      query: GET_ALL_SCHEDULES,
      variables: {
        page: filters.page,
        pageSize: filters.pageSize,
        screenId: filters.screenId,
        playlistId: filters.playlistId,
        status: filters.status,
      },
      fetchPolicy: "network-only",
    });
    return data.allSchedules;
  },
};
